import React from 'react';
import { View, Text } from 'react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';

interface FormFieldProps {
  /** Label displayed above the input */
  label: string;
  /** The input element(s) to render */
  children: React.ReactNode;
  /** Optional error message displayed below the input */
  error?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Additional className for the container */
  className?: string;
}

/**
 * Label + input wrapper with optional error message.
 * Extracted from form patterns in portfolio and company screens.
 *
 * Usage:
 * ```tsx
 * <FormField label="Company Name" required error={errors.name}>
 *   <TextInput value={name} onChangeText={setName} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  children,
  error,
  required = false,
  className,
}: FormFieldProps) {
  const colors = useThemeColors();

  return (
    <View className={cn('mb-3', className)}>
      <Text
        style={{ color: colors.text.secondary }}
        className="text-sm font-medium mb-2"
        accessibilityRole="text"
      >
        {label}
        {required && (
          <Text style={{ color: colors.error }}> *</Text>
        )}
      </Text>
      {children}
      {error && (
        <Text
          style={{ color: colors.error }}
          className="text-xs mt-1"
          accessibilityRole="alert"
        >
          {error}
        </Text>
      )}
    </View>
  );
}
